// Shared client logic for newsletter subscription (floating nudge + footer
// form). Both surfaces POST to /api/newsletter/subscribe, which proxies to
// ListMonk server-side — the API token never reaches the browser.

import { useState, useEffect, type FormEvent } from 'react';
import { trackEvent } from '@/lib/plausible';

export type SubscribeState = 'idle' | 'submitting' | 'success' | 'error';

export interface SubscribeResult {
  ok: boolean;
  status: number;
  alreadySubscribed?: boolean;
  reason?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value);
}

export async function subscribeToNewsletter(input: {
  email: string;
  name?: string;
  source?: string;
}): Promise<SubscribeResult> {
  try {
    const res = await fetch('/api/newsletter/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        email: input.email,
        name: input.name,
        source: input.source,
        website: '', // honeypot — always empty for real submissions
      }),
    });
    const data = (await res.json().catch(() => ({}))) as {
      alreadySubscribed?: boolean;
      error?: string;
    };
    return {
      ok: res.ok,
      status: res.status,
      alreadySubscribed: !!data.alreadySubscribed,
      reason: data.error,
    };
  } catch {
    return { ok: false, status: 0, reason: 'network' };
  }
}

/**
 * Subscription form state machine shared by the nudge and footer forms.
 * `source` is recorded for analytics and passed to the API.
 */
export function useNewsletterSubscribe(source: string) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState<SubscribeState>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!isValidEmail(email)) {
      setError('Please enter a valid email');
      return;
    }
    setError(null);
    setState('submitting');

    const result = await subscribeToNewsletter({ email, name: name.trim() || undefined, source });

    if (result.ok || result.status === 503) {
      // 503 = integration disabled server-side; treat as a soft no-op success so
      // the visitor isn't shown a confusing error.
      setState('success');
      trackEvent('Newsletter Subscribe', { location: source });
    } else if (result.status === 429) {
      setState('error');
      setError('Too many attempts. Please try again in a little while.');
    } else {
      setState('error');
      setError('Subscription failed. Please try again later.');
    }
  };

  return { email, setEmail, name, setName, state, error, setError, submit };
}

// ─── Admin helpers (require an admin session) ─────────────────────────

export interface NewsletterStatus {
  ok: boolean;
  subscriberCount?: number;
  listName?: string;
  lastCampaign?: { name: string; status: string; createdAt: string } | null;
  reason?: string;
}

export async function fetchNewsletterStatus(): Promise<NewsletterStatus> {
  try {
    const res = await fetch('/api/newsletter/admin/status', { headers: { Accept: 'application/json' } });
    return (await res.json()) as NewsletterStatus;
  } catch {
    return { ok: false, reason: 'network' };
  }
}

export interface TestConnectionResult {
  valid: boolean;
  reason?: string;
}

export async function testNewsletterConnection(): Promise<TestConnectionResult> {
  try {
    const res = await fetch('/api/newsletter/admin/test-connection', {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    return (await res.json()) as TestConnectionResult;
  } catch {
    return { valid: false, reason: 'network' };
  }
}

export interface SendArticleResult {
  ok: boolean;
  status: number;
  campaignId?: number;
  error?: string;
  reason?: string;
  sentAt?: string | number | null;
}

/** Manually send (or resend) an article as a ListMonk campaign. */
export async function sendArticleNewsletter(slug: string, resend = false): Promise<SendArticleResult> {
  try {
    const res = await fetch(
      `/api/newsletter/admin/send/${encodeURIComponent(slug)}${resend ? '?resend=1' : ''}`,
      { method: 'POST', headers: { Accept: 'application/json' } },
    );
    const data = (await res.json().catch(() => ({}))) as {
      campaignId?: number;
      error?: string;
      reason?: string;
      sentAt?: string | number | null;
    };
    return {
      ok: res.ok,
      status: res.status,
      campaignId: data.campaignId,
      error: data.error,
      reason: data.reason,
      sentAt: data.sentAt,
    };
  } catch {
    return { ok: false, status: 0, reason: 'network' };
  }
}

/** Reads the public `enabled` flag so the footer form can self-hide. */
export function useNewsletterEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    fetch('/api/newsletter/enabled', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((d: { enabled?: boolean }) => {
        if (active) setEnabled(!!d.enabled);
      })
      .catch(() => {
        /* leave disabled */
      });
    return () => {
      active = false;
    };
  }, []);
  return enabled;
}
